import { Flex, Paper, Text, Badge, Box, Divider, Button } from "@mantine/core";
import ReadMoreText from "@/components/shared/ReadMoreText";
import {
  IconBuilding,
  IconMapPin,
  IconTool,
  IconUser,
  IconShieldCheck,
  IconBriefcase,
  IconTarget,
  IconUserCheck,
  IconDownload,
} from "@tabler/icons-react";
import S3Image from "@/components/shared/S3Image";
import ProductsServicesTab from "@/components/shared/AnonymousCompanyProfile/ProductsServicesTab";
import { getFileUrl } from "@/shared/utils/useFileUrl";
import {
  getIndustryLabel,
  getSubIndustryLabel,
  getCollaborationIntentLabel,
  getCollaborationRoleLabel,
  getWhoAmILabel,
  getAdditionalTechInfraLabel,
  FileCategory,
  regionsData,
} from "@/components/shared/constants/general.const";

interface MatchedProfileTabProps {
  company: any;
  collaboration?: any;
}

export default function MatchedProfileTab({
  company,
  collaboration,
}: MatchedProfileTabProps) {
  const getRegionLabel = (regionId: string): string => {
    const region = regionsData.regions.find((r) => r.id === regionId);
    return region ? region.name : regionId;
  };

  const metadata = company.externalVerificationLogs?.[0]?.metadata;

  return (
    <>
      {/* Collaboration Intent Details */}
      {collaboration?.requesterRole && (
        <Paper p={{ base: "md", sm: "xl" }} radius="lg" mb="xl" style={{ border: "1px solid #E5E7EB" }}>
          <Flex align="center" gap="xs" mb={4}>
            <IconBriefcase size={18} color="#2563EB" />
            <Text size="lg" fw={400}>
              Collaboration Intent Details
            </Text>
          </Flex>
          <Text size="xs" c="slate.5" mb="lg">
            Information provided by the company about their collaboration goals
          </Text>

          <Flex direction="column" gap="md">
            {/* Collaboration type they're exploring */}
            {Array.isArray(company.collaborationIntent) && company.collaborationIntent.length > 0 && (
              <Box>
                <Flex align="center" gap={6} mb="xs">
                  <IconTarget size={14} color="var(--mantine-color-violet-6)" />
                  <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                    Collaboration type they're exploring
                  </Text>
                </Flex>
                <Flex gap="xs" wrap="wrap">
                  {company.collaborationIntent.map((intent: string, i: number) => (
                    <Badge
                      key={i}
                      variant="light"
                      color="#E9D4FF"
                      bg="#FAF5FF"
                      c="#8200DB"
                      py={10}
                      px={8}
                      size="md"
                      fw={400}
                      style={{ border: "1px solid #E9D4FF" }}
                    >
                      {getCollaborationIntentLabel(intent)}
                    </Badge>
                  ))}
                </Flex>
              </Box>
            )}

            {/* Their role in this collaboration */}
            <Box>
              <Flex align="center" gap={6} mb="xs">
                <IconBriefcase size={14} color="var(--mantine-color-teal-6)" />
                <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                  Their role in this collaboration
                </Text>
              </Flex>
              <Flex gap="xs" wrap="wrap">
                <Badge
                  variant="light"
                  color="#B9F8CF"
                  bg="#F0FDF4"
                  c="#008236"
                  py={10}
                  px={8}
                  size="md"
                  fw={400}
                  style={{ border: "1px solid #B9F8CF" }}
                >
                  {getCollaborationRoleLabel(collaboration.requesterRole)}
                </Badge>
              </Flex>
            </Box>

            {/* What they expect from you */}
            {collaboration.expectedPartnerRole && (
              <Box>
                <Flex align="center" gap={6} mb="xs">
                  <IconUserCheck size={14} color="var(--mantine-color-yellow-7)" />
                  <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                    What they expect from you
                  </Text>
                </Flex>
                <Flex gap="xs" wrap="wrap">
                  <Badge
                    variant="light"
                    color="#FEE685"
                    bg="#FFFBEB"
                    c="#BB4D00"
                    py={10}
                    px={8}
                    size="md"
                    fw={400}
                    style={{ border: "1px solid #FEE685" }}
                  >
                    {getCollaborationRoleLabel(collaboration.expectedPartnerRole)}
                  </Badge>
                </Flex>
              </Box>
            )}
          </Flex>
        </Paper>
      )}

      {/* Business Overview */}
      <Paper p={{ base: "md", sm: "xl" }} radius="lg" mb="xl" style={{ border: "1px solid #E5E7EB" }}>
        <Flex align="center" gap="xs" mb="md">
          <IconBuilding size={18} color="#2563EB" />
          <Text size="lg" fw={400}>
            Business Overview
          </Text>
        </Flex>

        <Flex direction="column" gap="lg">
          {/* Two Column Layout */}
          <Flex gap="xl" wrap="wrap">
            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px", maxWidth: "50%" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                COMPANY DESCRIPTION
              </Text>
              <ReadMoreText text={company.description || ""} />
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                COUNTRY
              </Text>
              <Text size="sm">{company.headquartersCountry || "N/A"}</Text>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                BUSINESS ID
              </Text>
              <Text size="sm">{company.gtid || company.registrationNumber || "N/A"}</Text>
            </Flex>

            {/* <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                YEARS OF OPERATION
              </Text>
              <Text size="sm">{getYearsOfOperation()}</Text>
            </Flex> */}

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                TRADE NAME
              </Text>
              <Text size="sm">{company.tradeName || "N/A"}</Text>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                INDUSTRY CATEGORY
              </Text>
              <Text size="sm">
                {Array.isArray(company.industryCategory) && company.industryCategory.length > 0
                  ? company.industryCategory.map((c: string) => getIndustryLabel(c)).join(", ")
                  : "N/A"}
              </Text>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                SUB CATEGORY
              </Text>
              <Text size="sm">
                {Array.isArray(company.subIndustry) && company.subIndustry.length > 0
                  ? company.subIndustry.map((sub: string) => getSubIndustryLabel(sub)).join(", ")
                  : "N/A"}
              </Text>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                COLLABORATION INTENT
              </Text>
              <Text size="sm">
                {Array.isArray(company.collaborationIntent) && company.collaborationIntent.length > 0
                  ? company.collaborationIntent.map((intent: string) => getCollaborationIntentLabel(intent)).join(", ")
                  : "N/A"}
              </Text>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                WHO AM I
              </Text>
              <Text size="sm">
                {Array.isArray(company.whoAmI) && company.whoAmI.length > 0
                  ? company.whoAmI.map((role: string) => getWhoAmILabel(role)).join(", ")
                  : "N/A"}
              </Text>
            </Flex>
          </Flex>

          {/* Company Gallery */}
          {company.images &&
            company.images.filter((img: any) => img.category === FileCategory.COMPANY_IMAGE).length > 0 && (
              <Box>
                <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
                  COMPANY GALLERY
                </Text>
                <Flex gap="sm" wrap="wrap">
                  {company.images
                    .filter((img: any) => img.category === FileCategory.COMPANY_IMAGE)
                    .map((image: any, index: number) => (
                      <Box key={index} style={{ width: 100, height: 100 }}>
                        <S3Image
                          fileId={image.id}
                          alt={`Company image ${index + 1}`}
                          radius="lg"
                          fit="cover"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            border: "1px solid #E5E7EB",
                          }}
                        />
                      </Box>
                    ))}
                </Flex>
                <Text size="xs" c="slate.6" mt="xs">
                  Facility photos and product images visible after verification
                </Text>
              </Box>
            )}
        </Flex>
      </Paper>

      {/* Products & Services */}
      <ProductsServicesTab company={company} isMatchedView={true} />

      {/* Capabilities & Offerings */}
      <Paper p={{ base: "md", sm: "xl" }} radius="lg" mb="xl" style={{ border: "1px solid #E5E7EB" }}>
        <Flex align="center" gap="xs" mb="md">
          <IconTool size={18} color="#2563EB" style={{ transform: "scaleX(-1)" }} />
          <Text size="lg" fw={400}>
            Capabilities & Offerings
          </Text>
        </Flex>

        <Flex direction="column" gap="lg">
          {/* Core Capabilities */}
          <Box>
            <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
              CORE CAPABILITIES
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.coreCapabilities || []).map((capability: string, index: number) => (
                <Badge key={index} variant="light" color="slate.6" c="black" py={10} px={8} size="md" fw={400}>
                  {capability}
                </Badge>
              ))}
              {(!company.coreCapabilities || company.coreCapabilities.length === 0) && (
                <Text size="sm" c="slate.6">
                  N/A
                </Text>
              )}
            </Flex>
          </Box>

          {/* Secondary Capabilities */}
          {company.secondaryCapabilities && company.secondaryCapabilities.length > 0 && (
            <Box>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
                SECONDARY CAPABILITIES
              </Text>
              <Flex gap="xs" wrap="wrap">
                {company.secondaryCapabilities.map((capability: string, index: number) => (
                  <Badge key={index} variant="light" color="slate.6" c="black" py={10} px={8} size="md" fw={400}>
                    {capability}
                  </Badge>
                ))}
              </Flex>
            </Box>
          )}

          {/* Key Differentiators */}
          {company.keyDifferentiators && (
            <Box>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
                KEY DIFFERENTIATORS
              </Text>
              <Text size="sm">{company.keyDifferentiators}</Text>
            </Box>
          )}

          {/* Technology & Infrastructure */}
          <Box>
            <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
              TECHNOLOGY & INFRASTRUCTURE
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.technologies || []).map((tech: string, index: number) => (
                <Badge key={index} variant="light" color="slate.6" c="black" py={10} px={8} size="md" fw={400}>
                  {tech}
                </Badge>
              ))}
              {(!company.technologies || company.technologies.length === 0) && (
                <Text size="sm" c="slate.6">
                  N/A
                </Text>
              )}
            </Flex>
          </Box>

          {/* Additional Technology & Infrastructure */}
          {company.additionalTechInfrastructure && company.additionalTechInfrastructure.length > 0 && (
            <Box>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
                ADDITIONAL TECHNOLOGY & INFRASTRUCTURE
              </Text>
              <Flex gap="xs" wrap="wrap">
                {company.additionalTechInfrastructure.map((item: string, index: number) => (
                  <Badge key={index} variant="light" color="slate.6" c="black" py={10} px={8} size="md" fw={400}>
                    {getAdditionalTechInfraLabel(item)}
                  </Badge>
                ))}
              </Flex>
            </Box>
          )}
        </Flex>
      </Paper>

      {/* Geography & Markets */}
      <Paper p={{ base: "md", sm: "xl" }} radius="lg" mb="xl" style={{ border: "1px solid #E5E7EB" }}>
        <Flex align="center" gap="xs" mb="md">
          <IconMapPin size={18} color="#2563EB" />
          <Text size="lg" fw={400}>
            Geography & Markets
          </Text>
        </Flex>

        <Flex direction="column" gap="lg">
          {/* Headquarters */}
          <Flex gap="xl" wrap="wrap">
            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
                HEADQUARTERS
              </Text>
              <Text size="sm">
                {company.headquartersCity && company.headquartersCountry
                  ? `${company.headquartersCity}, ${company.headquartersCountry}`
                  : company.headquartersCountry || "N/A"}
              </Text>
            </Flex>

            {/* Operating Countries */}
            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
                OPERATING COUNTRIES
              </Text>
              <Flex gap="xs" wrap="wrap">
                {(company.operatingCountries || []).map((country: string, index: number) => (
                  <Badge key={index} variant="light" color="slate.6" c="black" py={10} px={8} size="md" fw={400}>
                    {country}
                  </Badge>
                ))}
                {(!company.operatingCountries || company.operatingCountries.length === 0) && (
                  <Text size="sm" c="slate.6">
                    N/A
                  </Text>
                )}
              </Flex>
            </Flex>
          </Flex>

          {/* Preferred Collaboration Regions */}
          <Box>
            <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
              PREFERRED COLLABORATION REGIONS
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.preferredCollaborationRegions || []).map((region: string, index: number) => (
                <Badge key={index} variant="light" color="slate.6" c="black" py={10} px={8} size="md" fw={400}>
                  {getRegionLabel(region)}
                </Badge>
              ))}
              {(!company.preferredCollaborationRegions || company.preferredCollaborationRegions.length === 0) && (
                <Text size="sm" c="slate.6">
                  N/A
                </Text>
              )}
            </Flex>
          </Box>
        </Flex>
      </Paper>

      {/* Contact Person Details */}
      <Paper p={{ base: "md", sm: "xl" }} radius="lg" mb="xl" style={{ border: "1px solid #E5E7EB" }}>
        <Flex align="center" gap="xs" mb="md">
          <IconUser size={18} color="#2563EB" />
          <Text size="lg" fw={400}>
            Contact Person
          </Text>
        </Flex>

        {(() => {
          const mandatoryDocs = (company.documents || []).filter((d: any) =>
            ["AFFIDAVIT", "AUTHORITY_LETTER"].includes(d.category)
          );
          const handleDownload = async (doc: any) => {
            const url = await getFileUrl(doc.id, true);
            if (url) window.open(url, "_blank");
          };
          const categoryLabel = (cat: string) => (cat === "AUTHORITY_LETTER" ? "Authority Letter" : "Affidavit");
          return (
            <Flex direction="column" gap="lg">
              <Flex gap="xl" wrap="wrap">
                <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
                  <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                    NAME
                  </Text>
                  <Text size="sm">{company.user?.name || "N/A"}</Text>
                </Flex>

                <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
                  <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                    DESIGNATION
                  </Text>
                  <Text size="sm">{company.user?.designation || "N/A"}</Text>
                </Flex>

                <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
                  <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                    PHONE NUMBER
                  </Text>
                  <Text size="sm">{company.user?.phoneNumber || "N/A"}</Text>
                </Flex>

                {mandatoryDocs.map((doc: any) => (
                  <Flex key={doc.id} direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
                    <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                      {categoryLabel(doc.category)}
                    </Text>
                    <Flex align="center" gap="xs">
                      <Text size="sm">
                        {doc.createdAt
                          ? `Uploaded on ${new Date(doc.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
                          : "—"}
                      </Text>
                      <Button
                        variant="subtle"
                        color="primary.7"
                        size="xs"
                        leftSection={<IconDownload size={14} />}
                        onClick={() => handleDownload(doc)}
                      >
                        Download
                      </Button>
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          );
        })()}
      </Paper>

      {/* System Verification Details */}
      {metadata && (
        <Paper
          p={{ base: "md", sm: "xl" }}
          radius="lg"
          mb="xl"
          style={{ border: "1px solid #E5E7EB" }}
        >
          <Flex align="center" gap="xs" mb="md">
            <IconShieldCheck size={18} color="#2563EB" />
            <Text size="lg" fw={400}>
              System Verification Details
            </Text>
          </Flex>

          <Flex gap="xl" wrap="wrap">
            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
                COMPANY NAME
              </Text>
              <Text size="sm">{metadata.companyName || "-"}</Text>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
                COMPANY ADDRESS
              </Text>
              <Text size="sm">{metadata.companyAddress || "-"}</Text>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
                DATE OF INCORPORATION
              </Text>
              <Text size="sm">{metadata.dateOfIncorporation || "-"}</Text>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
                REGISTRATION NUMBER
              </Text>
              <Text size="sm">{metadata.registrationNumber || "-"}</Text>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
                COMPANY TYPE
              </Text>
              <Text size="sm">{metadata.companyType || "-"}</Text>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
                COMPANY STATUS
              </Text>
              <Text size="sm">{metadata.companyStatus || "-"}</Text>
            </Flex>
          </Flex>
        </Paper>
      )}

    </>
  );
}
